import { useTranslation } from 'react-i18next'
import { useContext, useEffect } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { Breadcrumbs, ListItemButton, Typography } from '@mui/material'

import { config } from '../config'
import { UserContext } from '../contexts'
import { CustomerCompany } from '../types'
import { useSocket } from '../hooks/use-socket'
import NextIcon from '../assets/icons/next-icon.svg'
import { handleDeleteDataByOne, handleGetDataFromTableById } from '../utils/db/common-methods'
import { CustomerCompanyFundingsHistory } from '../components/customer-company-details-management/customer-company-fundings-history'

const CustomerCompanyFundingsHistoryManagement = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { customerCompanyId } = useParams()
  const { userSession } = useContext(UserContext)
  const { appDataSocket, isAppDataSocketInitialized } = useSocket()
  const { t } = useTranslation(['customer-company-details-management'])

  useEffect(() => {
    if (!userSession) {
      return
    }

    const isAllowedToThisSection =
      (userSession && userSession?.role.indexOf('super-admin') > -1) ||
      (userSession && userSession?.role.indexOf('super-admin') === -1 && userSession?.role.indexOf('admin') > -1) ||
      (userSession && userSession?.role === 'admin-client' && userSession.belongsTo === String(config.app.paxumCompanyId))

    if (!isAllowedToThisSection) {
      navigate('/404')
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSession])

  useEffect(() => {
    if (!appDataSocket) return

    appDataSocket.on('deleteCustomerCompany', async (dataFromSocket: any) => {
      if (String(dataFromSocket.documentKey) === String(customerCompanyId)) {
        handleDeleteCustomerCompanyFromLocalDB(String(dataFromSocket.documentKey))
      }
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appDataSocket, isAppDataSocketInitialized, customerCompanyId])

  const handleDeleteCustomerCompanyFromLocalDB = async (documentKey: string) => {
    const customerCompanyDataSetted: Array<CustomerCompany> = (await handleGetDataFromTableById('customer-companies', String(documentKey))) as Array<CustomerCompany>

    if (customerCompanyDataSetted.length > 0) {
      await handleDeleteDataByOne('customer-companies', documentKey)
    }

    navigate('/')
  }

  return (
    <div className="section-container">
      <Breadcrumbs
        separator={
          <img
            src={NextIcon}
            alt="next-icon"
          />
        }
        sx={{ width: '100%' }}
        aria-label="breadcrumb"
      >
        <ListItemButton
          sx={{
            '&.MuiButtonBase-root': {
              padding: '0px'
            },
            '&.MuiButtonBase-root:hover': {
              backgroundColor: 'rgba(0,0,0,0)'
            }
          }}
          onClick={() => {
            if (location.state?.previousPath && location.state?.previousPath === 'insights') {
              navigate('/insights')
              return
            }

            navigate('/customer-companies')
          }}
        >
          {location.state?.previousPath && location.state?.previousPath === 'insights' ? (
            <Typography
              key="3"
              className="breadcrumb-inactive"
            >
              {t('breadcrumbs.insights')}
            </Typography>
          ) : (
            <Typography
              key="3"
              className="breadcrumb-inactive"
            >
              {t('breadcrumbs.firstLevel')}
            </Typography>
          )}
        </ListItemButton>
        {(!location.state?.previousPath || (location.state?.previousPath && location.state?.previousPath !== 'insights')) && (
          <ListItemButton
            sx={{
              '&.MuiButtonBase-root': {
                padding: '0px'
              },
              '&.MuiButtonBase-root:hover': {
                backgroundColor: 'rgba(0,0,0,0)'
              }
            }}
            onClick={() => navigate(`/customer-company-details/${customerCompanyId}`)}
          >
            <Typography
              key="3"
              className="breadcrumb-inactive"
            >
              {t('breadcrumbs.secondLevel')}
            </Typography>
          </ListItemButton>
        )}
        <Typography
          key="3"
          className="breadcrumb-active"
        >
          {t('breadcrumbs.thirdLevel')}
        </Typography>
      </Breadcrumbs>
      <CustomerCompanyFundingsHistory />
    </div>
  )
}

export default CustomerCompanyFundingsHistoryManagement
